import { useEffect, useRef, useState, type CSSProperties } from "react";
import { authClient } from "../lib/auth";
import { rememberSignedIn } from "../lib/returnTo";
import { Wordmark } from "../components/Wordmark";
import { Avatar } from "../components/ui";
import { SealedStrip, StaticMessage } from "../components/Discussion";
import { Envelope } from "../components/Moments";
import { home } from "@pagemates/shared/copy";

const txt = home.page;

/*
 * The signed-out home page (PRD "Pagemates home page", built against the mock
 * in artifact CnK2tF4wUqHVX7PEoCjisd). Prebuilt to plain HTML (prerender.tsx),
 * so it paints before any script or API call; the demo is the only part that
 * needs JavaScript. It is also the router's "/" (after signing out, say), so
 * links are plain <a>: nothing here depends on the router.
 *
 * Words: what happens to the reader, never the app's own names for things
 * (no Talk, sealed, position, shelf as a feature name, admin, PWA...).
 */

const START = "/signin?next=%2Fclubs%2Fnew";
const FIND = "/signin?next=%2Fclubs%2Fbrowse";

/** Signed in: straight to your clubs. An old sign-in link (/?next=…) goes on to the sign-in page with it. */
function useSendOn() {
  useEffect(() => {
    const next = new URLSearchParams(window.location.search).get("next");
    if (next) { window.location.replace(`/signin?next=${encodeURIComponent(next)}`); return; }
    void authClient.getSession().then(({ data }) => {
      rememberSignedIn(Boolean(data));
      if (data) window.location.replace("/clubs");
    }).catch(() => { /* offline: stay on the page */ });
  }, []);
}

export function Home() {
  useSendOn();
  return (
    <div className="home">
      <header className="h-top h-gut">
        <div className="h-in">
          <a href="/" className="h-wm" aria-label={txt.pagematesHome}><Wordmark size="sm" /></a>
          <a href="/signin" className="h-signin">{txt.signIn}</a>
        </div>
      </header>

      <main>
        <section className="h-hero h-gut">
          <div className="h-in">
            <div>
              <h1>{txt.aBookClubWhere}</h1>
              <p className="h-lede">{txt.readAtYourOwn}</p>
              <div className="h-ctas">
                <a className="btn primary" href={START}>{txt.startAClub}</a>
                <a className="btn" href={FIND}>{txt.findAClub}</a>
              </div>
              <p className="h-note">{txt.freeSignInWith}</p>
            </div>
            <Demo />
          </div>
        </section>

        <section className="h-band h-gut" aria-labelledby="how-h">
          <div className="h-in">
            <p className="h-eyebrow">{txt.howItWorks}</p>
            <h2 id="how-h">{txt.aClubTakesAbout}</h2>
            <ol className="h-steps">
              <li>{txt.startAClubAnd}</li>
              <li>{txt.everyoneReadsAtTheir}</li>
              <li>{txt.meetUpInPerson}</li>
            </ol>
          </div>
        </section>

        <section className="h-band h-gut" aria-labelledby="q-h">
          <div className="h-in">
            <p className="h-eyebrow">{txt.whatYouGet}</p>
            <h2 id="q-h">{txt.theQuestionsEveryBook}</h2>
            <div className="h-feats">
              <Feature q={txt.whatIfMyFriends} a={txt.theirCommentsWaitFor}>
                <StaticMessage name="Maya" pos={txt.page60} body={txt.theLettersInChapter} />
                <SealedStrip face={<Avatar name="Theo" />}><span><b>{txt.hiddenUntilPage180}</b></span></SealedStrip>
                <div className="h-waiting"><Envelope /><span>{txt.theoLeftYouA}</span></div>
              </Feature>
              <Feature q={txt.howDoWeKnow} a={txt.everyoneSPlaceIn}>
                <div className="h-track">
                  <span className="h-goal" style={{ left: "62%" }}><span>{txt.goalForThursday}</span></span>
                  <span className="h-face" style={{ left: "18%" }}><Avatar name="Sam" /></span>
                  <span className="h-face me" style={{ left: "41%" }}><span className="avatar">{txt.you}</span></span>
                  <span className="h-face" style={{ left: "55%" }}><Avatar name="Maya" /></span>
                  <span className="h-face" style={{ left: "79%" }}><Avatar name="Theo" /></span>
                </div>
                <p className="h-cap">{txt.about20PagesA}</p>
              </Feature>
              <Feature q={txt.howDoWeChoose} a={txt.anyoneCanSuggestOne}>
                <div className="h-vote">
                  {([[txt.northOfTheLighthouse, "#8a3a24", "#b04e30", 84, 11], [txt.theSaltArchive, "#3b3352", "#544a75", 60, 8], [txt.aYearOfSmall, "#9a7a2e", "#c29a3e", 30, 4]] as const).map(([t, a, b, w, pts]) => (
                    <div className="h-vrow" key={t}>
                      <Cover a={a} b={b} /><span className="n">{t}</span><span className="bar"><i style={{ width: `${w}%` }} /></span><span className="pts">{txt.points(pts)}</span>
                    </div>
                  ))}
                </div>
                <p className="h-cap">{txt.n4Of5Have}</p>
              </Feature>
              <Feature q={txt.whatHappensAtThe} a={txt.questionsPeopleSavedWhile}>
                <ul className="h-agenda">
                  <li className="done">{txt.wasTheGardenerLying}</li>
                  <li>{txt.whyDoesTheOrchard}</li>
                  <li>{txt.wouldYouHaveOpened}</li>
                </ul>
                <p className="h-cap">{txt.thursday630Pm}</p>
              </Feature>
              <Feature q={txt.whatDoWeEnd} a={txt.everyBookYouVe}>
                {/* The guess stands on the plank with the books, so the drawing holds together at every width. */}
                <div className="h-books h-books-guess">
                  {([["#1f4a52", "#2e6a72", 76], ["#8a3a24", "#b04e30", 84], ["#3b3352", "#544a75", 74], ["#9a7a2e", "#c29a3e", 80]] as const).map(([a, b, h], i) => <Cover key={i} a={a} b={b} style={{ width: 34, height: h }} />)}
                  <div className="paper-note h-guess">
                    <div className="from">{txt.samSGuessP}</div>
                    <p className="txt">{txt.theGardenerPlantedIt}</p>
                  </div>
                </div>
                <div className="h-plank"><span className="h-plate">2026</span></div>
                <p className="h-cap h-stars">{txt.clubAverage}<span aria-hidden="true">★★★★☆</span></p>
              </Feature>
            </div>
          </div>
        </section>

        <section className="h-band h-gut" aria-labelledby="find-h">
          <div className="h-in">
            <p className="h-eyebrow">{txt.findAClub}</p>
            <h2 id="find-h">{txt.orJoinOneThat}</h2>
            <p className="h-sub">{txt.someClubsWelcomeNew}</p>
            <div className="h-clubs">
              <ClubCard name="Lantern Readers" about={txt.slowBooksFastOpinions} meta={txt.n34MembersAbout20} a="#1f4a52" b="#2e6a72" />
              <ClubCard name="The Long Table" about={txt.oneBigClassicA} meta={txt.n57MembersRelaxedPace} a="#6b2e2e" b="#933f3a" />
              <ClubCard name="Orbit Club" about={txt.scienceFictionAndThe} meta={txt.n128MembersFastPace} a="#2e3a5c" b="#3f5285" />
            </div>
            <div className="h-row-end"><a className="btn" href={FIND}>{txt.browseClubs}</a><span className="h-cap">{txt.exampleClubsYouLl}</span></div>
          </div>
        </section>

        <section className="h-band h-gut" aria-labelledby="faq-h">
          <div className="h-in">
            <p className="h-eyebrow">{txt.questions}</p>
            <h2 id="faq-h">{txt.goodToKnow}</h2>
            <div className="h-faq">
              <details><summary>{txt.doesItCostAnything}</summary><p>{txt.no}</p></details>
              <details><summary>{txt.whoCanSeeWhat}</summary><p>{txt.onlyPeopleInYour}</p></details>
              <details><summary>{txt.whatDoINeed}</summary><p>{txt.aDiscordGoogleOr}</p></details>
              <details><summary>{txt.isThereAnApp}</summary><p>{txt.itWorksInAny}</p></details>
              <details><summary>{txt.whatSHardcover}</summary><p>{txt.aFreeSiteFor}</p></details>
            </div>
          </div>
        </section>

        <section className="h-closing h-gut">
          <h2>{txt.yourNextBookIs}</h2>
          <a className="btn primary" href={START}>{txt.startAClub}</a>
        </section>
      </main>

      <footer className="h-foot h-gut">
        <div className="h-in"><span>{txt.pagemates}</span><a href="/policy.html">{txt.policyAndContact}</a></div>
      </footer>
    </div>
  );
}

function Feature({ q, a, children }: { q: string; a: string; children: React.ReactNode }) {
  return (
    <div className="h-feat">
      <div><h3>{q}</h3><p>{a}</p></div>
      <div className="h-vig" aria-hidden="true">{children}</div>
    </div>
  );
}

/** A made-up book: two tones, spine and board. */
function Cover({ a, b, style }: { a: string; b: string; style?: CSSProperties }) {
  return <span className="h-cover" aria-hidden="true" style={{ background: `linear-gradient(100deg, ${a} 0 5px, ${b} 5px)`, ...style }} />;
}

function ClubCard({ name, about, meta, a, b }: { name: string; about: string; meta: string; a: string; b: string }) {
  return (
    <div className="h-club">
      <Cover a={a} b={b} />
      <div><b>{name}</b><span className="d">{about}</span><span className="m">{meta}</span></div>
    </div>
  );
}

/* ---------- the demo: a slider for how far you've read over a made-up conversation ---------- */

const BOOK_PAGES = 240;
const START_PAGE = 40;
const SAID = [
  { name: "Sam", page: 12, body: txt.tenPagesInAnd },
  { name: "Maya", page: 38, body: txt.theFirstLetterMade },
  { name: "Theo", page: 90, body: txt.waitSoTheGardener },
  { name: "Priya", page: 160, body: txt.thatNightInThe },
  { name: "Jun", page: 210, body: txt.iDidNotSee },
];
// One note left at a page, for whoever gets there: it waits in an envelope and opens as you pass it.
const NOTE = { name: "Maya", page: 120, body: txt.slowDownForThe };
const visibleAt = (page: number) => SAID.filter((m) => m.page <= page).length;
const messages = (n: number) => `${n} ${n === 1 ? txt.message : txt.messages}`;

function Demo() {
  const [page, setPage] = useState(START_PAGE);
  // Messages only fade in once someone has moved the slider, not on first paint.
  const [moved, setMoved] = useState(false);
  const [said, setSaid] = useState("");
  const last = useRef(visibleAt(START_PAGE));
  const open = visibleAt(page);
  const noteOpen = page >= NOTE.page;

  const move = (p: number) => {
    setPage(p); setMoved(true);
    const n = visibleAt(p), d = n - last.current;
    const lines = [];
    if (d > 0) lines.push(txt.moreVisible(d, d === 1 ? txt.message : txt.messages));
    else if (d < 0) lines.push(txt.hiddenAgain(messages(-d)));
    if (p >= NOTE.page && page < NOTE.page) lines.push(txt.leftYouANote(NOTE.name));
    else if (p < NOTE.page && page >= NOTE.page) lines.push(txt.theNoteIsWaiting);
    if (lines.length) setSaid(lines.join(". "));
    last.current = n;
  };

  const before = SAID.filter((m) => m.page < NOTE.page), after = SAID.filter((m) => m.page >= NOTE.page);
  const row = (m: typeof SAID[number]) => m.page <= page
    ? <StaticMessage key={`${m.name}-open`} name={m.name} pos={txt.page(m.page)} body={m.body} className={moved ? "arrive" : undefined} />
    : <SealedStrip key={`${m.name}-hidden`} face={<Avatar name={m.name} />}><span><b>{txt.hiddenUntilPage(m.page)}</b></span></SealedStrip>;

  return (
    <div className="h-demo" role="group" aria-labelledby="demo-h">
      <div className="h-demo-book">
        <Cover a="#1f4a52" b="#2e6a72" style={{ width: 34, height: 50 }} />
        <div><b id="demo-h">{txt.theGlassOrchard}</b><span>{txt.aMadeUpBook(BOOK_PAGES)}</span></div>
      </div>
      <div className="h-msgs">
        {before.map(row)}
        {noteOpen ? (
          <div key="note-open" className={`h-letter${moved ? " opening" : ""}`}>
            {moved && <Envelope />}
            <div className="paper-note">
              <div className="from">{txt.leftThisForYou(NOTE.name, NOTE.page)}</div>
              <p className="txt">{NOTE.body}</p>
            </div>
          </div>
        ) : (
          <div key="note-wait" className="h-waiting"><Envelope /><span>{txt.aNoteFromIs(NOTE.name, NOTE.page)}</span></div>
        )}
        {after.map(row)}
      </div>
      <div className="h-slider">
        <label htmlFor="demo-page"><span>{txt.youVeReadTo}<b>{txt.page(page)}</b></span><span className="muted">{txt.ofShowing(open, SAID.length)}</span></label>
        <input type="range" id="demo-page" min={0} max={BOOK_PAGES} step={1} value={page} aria-valuetext={txt.page(page)} aria-describedby="demo-cap"
          onChange={(e) => move(Number(e.target.value))} />
        <p className="h-demo-cap" id="demo-cap">{txt.dragToSeeWhat}</p>
        <p className="sr-only" aria-live="polite">{said}</p>
      </div>
    </div>
  );
}
